import type { SettingsContribution } from '@repo/commons/settings'
import { downloadContent } from '@repo/commons/utils'

export type ImportState = 'idle' | 'importing' | 'done' | 'error'

export interface ImportDeps {
	settings: ReturnType<typeof import('@repo/commons/settings').useSettings>
	contributionsData: () => SettingsContribution[]
}

export function useSettingsImport(
	deps: ImportDeps,
	onDraftUpdate: (draft: Record<string, unknown>) => void
) {
	const { settings } = deps

	let importState = $state<ImportState>('idle')
	let importMessage = $state('')
	let importErrorMessage = $state('')
	let importProgress = $state(0)
	let importFile = $state<{ name: string; size: number } | null>(null)
	let progressInterval = $state<ReturnType<typeof setInterval> | null>(null)
	let fileInput: HTMLInputElement | null = $state(null)

	function startProgressSimulation() {
		importProgress = 0
		progressInterval = setInterval(() => {
			if (importProgress < 90) {
				importProgress = Math.min(90, importProgress + 3 + Math.random() * 5)
			}
		}, 100)
	}

	function stopProgressSimulation() {
		if (progressInterval) {
			clearInterval(progressInterval)
			progressInterval = null
		}
	}

	function dismissImport() {
		importState = 'idle'
		importMessage = ''
		importErrorMessage = ''
		importProgress = 0
		importFile = null
	}

	function handleChooseAnotherFile() {
		dismissImport()
		fileInput?.click()
	}

	function handleExport() {
		downloadContent(settings.exportJSON(), 'settings.json', 'application/json')
	}

	function handleImportClick() {
		if (importState === 'importing') return
		fileInput?.click()
	}

	function handleImportFile(event: Event) {
		const input = event.target as HTMLInputElement
		const file = input.files?.[0]
		if (!file) return

		importFile = { name: file.name, size: file.size }

		if (!file.name.endsWith('.json')) {
			importState = 'error'
			importMessage = 'Settings import failed!'
			importErrorMessage = 'Only .json files are supported.'
			input.value = ''
			return
		}

		importState = 'importing'
		importMessage = 'Importing settings...'
		startProgressSimulation()

		const reader = new FileReader()

		reader.onerror = () => {
			stopProgressSimulation()
			importState = 'error'
			importMessage = 'Settings import failed!'
			importErrorMessage = 'The file could not be read.'
		}

		reader.onload = () => {
			setTimeout(() => {
				stopProgressSimulation()

				const result = settings.importJSON(reader.result as string)

				if (result.success) {
					settings.clearPreset()
					importProgress = 100
					const draft: Record<string, unknown> = {}
					const allDefs = deps.contributionsData().flatMap((c) => c.definitions)
					for (const def of allDefs) {
						draft[def.key] = settings.get(def.key)
					}
					onDraftUpdate(draft)

					setTimeout(() => {
						importState = 'done'
						importMessage = 'Settings imported'
					}, 400)

					setTimeout(() => {
						importState = 'idle'
						importMessage = ''
						importProgress = 0
					}, 2500)
				} else {
					importState = 'error'
					importMessage = 'Settings import failed!'
					importErrorMessage = result.errors[0] ?? "The uploaded file isn't a valid settings JSON."
				}

				if (result.errors.length > 0) {
					console.warn('[ViewSettings] Import warnings:', result.errors)
				}
			}, 1200)
		}

		reader.readAsText(file)

		input.value = ''
	}

	return {
		get state() {
			return importState
		},
		get message() {
			return importMessage
		},
		get errorMessage() {
			return importErrorMessage
		},
		get progress() {
			return importProgress
		},
		get file() {
			return importFile
		},
		get fileInput() {
			return fileInput
		},
		set fileInput(el: HTMLInputElement | null) {
			fileInput = el
		},
		handleExport,
		handleImportClick,
		handleImportFile,
		handleChooseAnotherFile
	}
}
